import React, { useEffect } from 'react';
import { useRoutes } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';

import publicRoutes from './routes/PublicRoutes';
import Header from './components/Header';
import Footer from './components/Footer';
import TopHeader from './components/TopHeader';
import AdSenseOptimized from './components/Ads/AdSenseOptimized';
import AdPerformanceMonitor from './components/Ads/AdPerformanceMonitor';
import useRPMOptimization from './hooks/useRPMOptimization';

const App: React.FC = () => {
  const routing = useRoutes(publicRoutes);
  const { rpmData, optimalAdCount, isHighValuePage } = useRPMOptimization();

  useEffect(() => {
    // Load GA with enhanced ad tracking
    const loadTrackingScripts = () => {
      const script1 = document.createElement('script');
      script1.src = 'https://www.googletagmanager.com/gtag/js?id=G-165Z34G9EG';
      script1.async = true;
      document.head.appendChild(script1);

      const script2 = document.createElement('script');
      script2.innerHTML = `
        window.dataLayer = window.dataLayer || [];
        function gtag(){dataLayer.push(arguments);}
        gtag('js', new Date());
        
        // Enhanced AI directory tracking
        gtag('config', 'G-165Z34G9EG', {
          page_title: document.title,
          page_path: window.location.pathname,
          page_category: 'ai_tools_directory',
          page_type: '${isHighValuePage ? 'premium_ai' : 'directory'}',
          estimated_rpm: ${isHighValuePage ? '8.5' : '5.5'},
          ad_optimization: 'enabled'
        });
        
        // Ad performance tracking
        gtag('event', 'page_view', {
          'ad_optimized': true,
          'ai_directory': true,
          'premium_page': ${isHighValuePage}
        });
      `;
      document.head.appendChild(script2);
    };

    if ('requestIdleCallback' in window) {
      requestIdleCallback(loadTrackingScripts);
    } else {
      setTimeout(loadTrackingScripts, 2000);
    }
  }, [isHighValuePage]);

  return (
    <div className="bg-white dark:bg-gray-900 text-black dark:text-white min-h-screen flex flex-col">
      <Helmet>
        <title>Best {rpmData?.category || 'AI Tools'} 2025 | TooliQ.ai</title>
        <meta name="description" content={`Find the best AI tools in ${rpmData?.category || 'all categories'} for 2025. Compare ${optimalAdCount}+ premium AI solutions.`} />
        
        {/* AI directory targeting */}
        <meta name="adsense-content-type" content="ai_tools_directory" />
        <meta name="adsense-rpm-tier" content={isHighValuePage ? 'premium' : 'standard'} />
        <meta name="adsense-keywords" content={rpmData?.keywords?.join(',') || 'ai-tools,artificial-intelligence'} />
        
        {/* Viewport ad optimization */}
        <meta name="viewport" content="width=device-width, initial-scale=1.0, maximum-scale=5.0" />
      </Helmet>

      <TopHeader />
      <Header />

      {/* Premium ad slot for high-value pages */}
      {isHighValuePage && (
        <div className="container mx-auto px-4 mt-4">
          <div className="bg-gradient-to-r from-blue-50 to-purple-50 dark:from-gray-800 dark:to-gray-900 p-2 rounded-lg border border-blue-200 dark:border-blue-800 mb-4">
            <div className="text-center text-xs text-blue-600 dark:text-blue-400 font-medium">
              🚀 Premium AI Tool Page - Higher Value Ads Active
            </div>
          </div>
        </div>
      )}

      {/* Header Ad */}
      <div className="container mx-auto px-4 my-4">
        <AdSenseOptimized
        slot="8084827009"
          format="leaderboard"
          className="header-ad-optimized"
        />
      </div>

      <main className="flex-1">
        {routing}
        
        {/* In-content ads with optimization */}
        <div className="container mx-auto px-4 my-8">
          <AdSenseOptimized
            slot="9014765293"
            format="rectangle"
            className="in-content-ad"
          />
        </div>
        
        {/* Premium middle ad for high-value pages */}
        {isHighValuePage && (
          <div className="container mx-auto px-4 my-8">
            <div className="bg-gradient-to-r from-purple-50 to-pink-50 dark:from-gray-800 dark:to-gray-900 rounded-xl p-4 border border-purple-200 dark:border-purple-800">
              <div className="text-center text-xs text-purple-600 dark:text-purple-400 mb-2">
                💎 Premium AI Tool Recommendations
              </div>
              <AdSenseOptimized
                slot="8768187433"
                format="leaderboard"
                className="premium-middle-ad"
              />
            </div>
          </div>
        )}
      </main>

      {/* Footer Ad */}
      <div className="container mx-auto px-4 mt-8">
        <AdSenseOptimized
          slot="7845153853"
          format="leaderboard"
          className="footer-ad"
        />
      </div>

      <Footer />
      
      {/* Performance monitor (development only) */}
      {/* {process.env.NODE_ENV === 'development' && <AdPerformanceMonitor />} */}
    </div>
  );
};

export default App;